import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { BehaviorSubject, combineLatest, Observable, of, Subject } from "rxjs";
import { map, take, takeUntil } from "rxjs/operators";
import { LandAreaFormGroup } from "src/app/components/land-areas/form-group";
import { createParcelIdentificationFormGroup, ParcelIdentificationFormGroup } from "src/app/components/parcel-identification/form-group";
import { AssetClassLandAreaModel } from "src/app/core/asset_class";
import { AssetClassSizeModel, AssetClassTenure } from "src/app/core/comparable";
import { AllExternalFacadesRequested, AllFoundationTypesRequested, AllGradesRequested, AllHandoverStandardsRequested, AllMainWallsRequested, AllRoofCoveringsRequested, AllStateRepairsRequested, AllUnitAreaMeasurementsRequested, ExternalFacadeModel, FoundationType, Grade, HandoverStandard, MainWallModel, RoofCoveringModel, selectAllExternalFacades, selectAllFoundationTypes, selectAllGrades, selectAllHandoverStandards, selectAllMainWalls, selectAllRoofCoverings, selectAllStateRepairs, selectAllUnitAreaMeasurements, StateRepair, UnitAreaMeasurement } from "src/app/core/linked-tables";
import { currentUser } from "src/app/core/mad-auth/mad-auth.store";
import { EsgIndicator } from "src/app/core/v2/state/esg-indicator/type";
import { AssetClassSize } from "src/app/core/v2/types";
import { formControlValueChanges } from "src/app/core/v2/utils";
import { AncillaryBuilding, SpecialPurposeAssetClass } from "src/app/core/v3/comparable/special-purpose";
import { SpecialPurposePropertyTypeService } from "src/app/core/v3/comparable/special-purpose/property-types";
import { CoordinatePoint } from "src/app/core/v3/coordinate-points";
import { LandArea, mapAssetClassLandAreaComponentToLandArea, mapLandAreaToAssetClassLandAreaComponent } from "src/app/core/v3/land-area";
import { LayoutUtilsService } from "src/app/core/_base/crud";
import { EsgIndicatorsComponent } from "../../../mad_shared/esg-indicators/esg-indicators.component";
import { AcDocumentUploadComponent } from "../../../shared_components/ac-document-upload/ac-document-upload.component";
import { AcGroundsComponent } from "../../../shared_components/ac-grounds/ac-grounds.component";
import { AccommodationLayoutTableComponent } from "../../../shared_components/accommodation-layout-table/accommodation-layout-table.component";
import { ExternalAspectsComponent } from "../../../shared_components/external-aspects/external-aspects.component";
import { InternalAspectsComponent } from "../../../shared_components/internal-aspects/internal-aspects.component";
import { MapLocationComponent } from "../../../shared_components/map-location/map-location.component";
import { ServicesAndInfrastructuresComponent } from "../../../shared_components/services-and-infrastructures/services-and-infrastructures.component";
import { UploadFileComponent } from "../../../shared_components/upload-file/upload-file.component";
import { ConsiderationAndSourceComponent } from "../../_sub/consideration-and-source/consideration-and-source.component";
import {v4 as uuidv4} from 'uuid'
import { updateFormState } from 'src/app/core/v2/forms/form-state';

@Component({
  selector: 'app-special-purpose-comparable-form',
  templateUrl: './special-purpose-comparable-form.component.html',
  styleUrls: ['./special-purpose-comparable-form.component.scss']
})
export class SpecialPurposeComparableFormComponent implements OnInit, OnDestroy {
  private _assetClass$ = new BehaviorSubject<SpecialPurposeAssetClass>(new SpecialPurposeAssetClass())
  @Input()
  set assetClass(value: SpecialPurposeAssetClass) {
    if (value === undefined || value === null) return;
    this.isSavedBefore = true
    this._assetClass$.next(value)
    this._markerInfo$.next({
      lat: value.location_data.latitude,
      lng: value.location_data.longitude,
      isShow: true
    })
    this._points$.next(value.points.map(item => ({latitude: Number(item.lat), longitude: Number(item.lng)})))
    this._landAreas$.next(value.landAreaComponents.map(item => mapAssetClassLandAreaComponentToLandArea(item)))

    const deepCopiedSizes = _deepCopy(value.sizes)
      .map(size => {
        size.uid = uuidv4()
        return size
      })
    const deepCopiedTenures = _deepCopy(value.tenures)
      .map(tenure => {
        const rentSizeUuid = deepCopiedSizes.find(size => size.id === tenure.rent_size_id)?.uid
        const expensesSizeUuid = deepCopiedSizes.find(size => size.id === tenure.expenses_size_id)?.uid
        tenure.rent_size_uid = rentSizeUuid
        tenure.expenses_size_uid = expensesSizeUuid
        return tenure
      })

    this.tenuresSubject.next(deepCopiedTenures)
    this.sizesSubject.next(deepCopiedSizes);
    this._buildings$.next(value.buildings)

    this.form.patchValue({
      property_type_id: value.property_type_id,
      property_grade_id: value.property_grade_id,
      building_name: value.building_name,
      completion_year: value.completion_year,
      above_floors: value.above_floors,
      below_floors: value.below_floors,
      handover_standard_id: value.handover_standard_id,
      state_of_repair_id: value.state_of_repair_id,
      foundation_type_id: value.foundation_type_id,
      has_ancillary_buildings: value.has_ancillary_buildings,
      construction_desc: value.construction_desc,
      anchor_tenant: value.anchor_tenant,
      developer: value.developer,
      parcel_identification: {
        add_info_on_land: value.add_info_on_land,
        boundary_type: value.boundary_type_id,
        land_parcel_name: value.land_parcel_name,
        coordinate_reference_system_id: value.coordinate_reference_system_id,
        land_area: {
          env_considerations: value.landArea.env_considerations,
          other_considerations: value.landArea.other_considerations,
          planning_status_id: value.landArea.planning_status_id,
          qos_degradation_id: value.landArea.qos_degradation_id
        }
      }
    })
    this.form.controls.ancillary_buildings_num.patchValue(
      value.buildings.length,
      {
        emitEvent: false,
      }
    )
    this._updateFormState()
  }

  private _formType$ = new BehaviorSubject<'simple'|'complex'>('simple')
  @Input()
  set formType(value: 'simple'|'complex') {
    this._formType$.next(value)
  }

  @ViewChild(MapLocationComponent, {static: false})
  mapLocationComponent: MapLocationComponent

  @ViewChild(AccommodationLayoutTableComponent, {static: false})
  accommodationLayoutTable: AccommodationLayoutTableComponent;

  @ViewChild(ServicesAndInfrastructuresComponent, {static: false})
  servicesAndInfraComponent: ServicesAndInfrastructuresComponent;

  @ViewChild(ExternalAspectsComponent, {static: false})
  externalAspectComponent: ExternalAspectsComponent;

  @ViewChild(InternalAspectsComponent, {static: false})
  internalAspectComponent: InternalAspectsComponent;

  @ViewChild(AcGroundsComponent, {static: true})
  acGroundsComponent: AcGroundsComponent;

  @ViewChild(EsgIndicatorsComponent, {static: false})
  esgIndicatorComponent: EsgIndicatorsComponent;

  @ViewChild(ConsiderationAndSourceComponent, {static: false})
  considerationAndSourceComponent: ConsiderationAndSourceComponent;

  @ViewChild('simple')
  public uploadFileComponent: UploadFileComponent;

  @ViewChild(AcDocumentUploadComponent, {static: false})
  acDocumentUploadComponent: AcDocumentUploadComponent;

  // Form
  form = new FormGroup({
    property_type_id: new FormControl<number>(null, [Validators.required]),
    completion_year: new FormControl<string>(null),
    building_name: new FormControl<string>(null),
    property_grade_id: new FormControl<number>(null),
    above_floors: new FormControl<number>(null),
    below_floors: new FormControl<number>(null),
    handover_standard_id: new FormControl<number>(null),
    state_of_repair_id: new FormControl<number>(null),
    foundation_type_id: new FormControl<number>(null),
    developer: new FormControl<string>(null),
    anchor_tenant: new FormControl<string>(null),
    has_ancillary_buildings: new FormControl<boolean>(false),
    ancillary_buildings_num: new FormControl<number>(0),
    construction_desc: new FormControl<string>(null),
    parcel_identification: createParcelIdentificationFormGroup({
      addInfoOnLand: false,
      boundaryType: 1,
      coordinateReferenceSystemId: null,
      landArea: {
        envConsiderations: null,
        otherConsiderations: null,
        planningStatusId: null,
        qosDegradationId: null,
        plotArea: null
      },
      landParcelName: null,
    }),
  })
  private _formState$ = new BehaviorSubject<FormState>({
    map_location: {
      invalid: undefined,
      requiredFields: 0
    },
    about_property: {
      invalid: undefined,
      requiredFields: 0
    },
    considerations: {
      invalid: undefined,
      requiredFields: 0
    },
    size: {
      invalid: undefined,
      requiredFields: 0
    },
    pictures: {
      invalid: undefined,
      requiredFields: 0
    }
  })
  hasFormErrors = false;
  isSavedBefore = false
  purposeTypes$ = this._purposeTypesService.types$
  grades: Grade[] = []
  handoverStandards: HandoverStandard[] = []
  stateOfRepairs: StateRepair[] = []
  foundationTypes: FoundationType[] = []
  considerationError$ = new BehaviorSubject<boolean>(undefined);
  showTitleRequiredError$ = new BehaviorSubject<boolean>(false)
  mainwalls: MainWallModel[] = [];
  externalFacades: ExternalFacadeModel[] = [];
  roofCoverings: RoofCoveringModel[] = [];
  unitAreaMeasurements: UnitAreaMeasurement[] = []

  // Tab
  selectedTab = 0;
  formTabHeaders$ = this._formState$.asObservable().pipe(
    map(state => this._mapTabs(state))
  )

  // State
  private readonly _currentUser$ = this._ngStore.select(currentUser)
  centerLat = null
  centerLng = null
  private _markerInfo$: BehaviorSubject<{lat: number, lng: number, isShow: boolean}> = new BehaviorSubject({lat: undefined, lng: undefined, isShow: false});
  markerInfo$ = this._markerInfo$.asObservable()
  private _points$ = new BehaviorSubject<CoordinatePoint[]>([])
  coordinatePoints$ = this._points$.asObservable()
  private _landAreas$ = new BehaviorSubject<LandArea[]>([])
  landAreas$ = this._landAreas$.asObservable()
  assetClass$ = this._assetClass$.asObservable()
  isComplexForm$ = this._formType$.asObservable().pipe(
    map(formType => formType === 'complex')
  )
  locationData$ = this.assetClass$.pipe(
    map(assetClass => assetClass.location_data)
  )
  aboveFloor$: BehaviorSubject<number> = new BehaviorSubject(0);
  belowFloor$: BehaviorSubject<number> = new BehaviorSubject(0);
  floors$: Observable<number[]> = combineLatest([
    this.aboveFloor$.asObservable(),
    this.belowFloor$.asObservable()
  ]).pipe(
    map(([above, below]) => {
      const l = -1 * (below ?? 0);
      const h = above ?? 0;
      const numbers: number[] = [];
      for (let i = l; i <= h; i++) {
        numbers.push(i);
      }
      return numbers;
    })
  ); 
  accomodationData$ = this.assetClass$.pipe(
    map(assetClass => assetClass.accommodation.data)
  )
  accomodationOthers$ = this.assetClass$.pipe(
    map(assetClass => assetClass.accommodation.others)
  )
  serviceAndInfraData$ = this.assetClass$.pipe(
    map(assetClass => assetClass.serviceAndInfraData)
  )
  externalAspectSectionTitle$ = formControlValueChanges(this.form.controls.has_ancillary_buildings).pipe(
    map(value => value ? '(Main Building) External Aspect' : 'External Aspect')
  )
  externalAspectData$ = this.assetClass$.pipe(
    map(assetClass => assetClass.externalAspectData)
  )
  internalAspectSectionTitle$ = formControlValueChanges(this.form.controls.has_ancillary_buildings).pipe(
    map(value => value ? '(Main Building) Internal Aspect' : 'Internal Aspect')
  )
  internalAspectData$ = this.assetClass$.pipe(
    map(assetClass => assetClass.internalAspectData)
  )
  groundsSectionTitle$ = formControlValueChanges(this.form.controls.has_ancillary_buildings).pipe(
    map(value => value ? '(Main Building) Grounds' : 'Grounds')
  )
  groundsData$ = this.assetClass$.pipe(
    map(assetClass => assetClass.grounds)
  )
  sizesSectionTitle$ = formControlValueChanges(this.form.controls.has_ancillary_buildings).pipe(
    map(value => value ? '(Main Building) Sizes' : 'Sizes')
  )
  sizesSubject = new BehaviorSubject<AssetClassSizeModel[]>([])
  esgIndicatorData$ = this.assetClass$.pipe(
    map(assetClass => assetClass.esgIndicator)
  )
  userUnitOfMeasurementId$ = this._currentUser$.pipe(
    map(user => {
      if (!user) return null 
      return user.settings.unit_of_measurement_id
    })
  )
  tenuresSubject = new BehaviorSubject<AssetClassTenure[]>([])
  comps = new BehaviorSubject([]);
  hasParcel = of(false);
  timezoneOffset: string | null
  sizes$ = combineLatest([ 
    this._ngStore.select(selectAllUnitAreaMeasurements),
    this.sizesSubject.asObservable()
  ]).pipe(
    map(([unitAreaMeasurements, sizes]) => {
      const items: AssetClassSize[] = sizes.map(size => ({
        id: size.id,
        uid: size.uid,
        size: size.size,
        standardMeasurementName: size.standard_measurement_name,
        unitAreaMeasurementAcronym: unitAreaMeasurements.find(item => item.id === size.unit_of_area_measurement_id)?.acronym
      }))
      return items
    })
  )
  acPictures$ = this.assetClass$.pipe(
    map(assetClass => assetClass.ac_pictures)
  )
  picture$ = this.assetClass$.pipe(
    map(assetClass => assetClass.picture)
  )
  documents$ = this.assetClass$.pipe(
    map(assetClass => assetClass.documents)
  )
  private _buildings$ = new BehaviorSubject<AncillaryBuilding[]>([])
  buildings$ = this._buildings$.asObservable()
  numberOfBuildings$ = this.buildings$.pipe(map(items => items.length)) 


  private _onDestroy$ = new Subject<void>()

  constructor(
    private _ngStore: Store,
    private _purposeTypesService: SpecialPurposePropertyTypeService,
    private _layoutUtilsService: LayoutUtilsService
  ) { 
  }

  ngOnInit(): void {
    formControlValueChanges(this.form.controls.has_ancillary_buildings).pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(value => {
      if (value) {
        this.form.controls.ancillary_buildings_num.setValidators(Validators.required)
      } else {
        this.form.controls.ancillary_buildings_num.removeValidators(Validators.required)
      }
      this.form.controls.ancillary_buildings_num.updateValueAndValidity({emitEvent: false})
    })

    formControlValueChanges(this.form.controls.above_floors).pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(value => this.aboveFloor$.next(value))

    formControlValueChanges(this.form.controls.below_floors).pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(value => this.belowFloor$.next(value))

    this.form.controls.ancillary_buildings_num.valueChanges.pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(value => {
      const currentBuildings = this._buildings$.getValue()
      if (value < currentBuildings.length) {
        const buildings = currentBuildings.slice(0, value)
        this._buildings$.next(buildings)
        return;
      }
      if (value > currentBuildings.length) {
        const extra = Array.from({
          length: value - currentBuildings.length
        }, (_, i) => createAncillaryBuilding())
        const buildings = [...currentBuildings, ...extra]
        this._buildings$.next(buildings)
        return;
      }
    })

    this._ngStore.dispatch(new AllGradesRequested())
    this._ngStore.pipe(select(selectAllGrades), takeUntil(this._onDestroy$)).subscribe(items => {
      this.grades = items ? items : []
    })

    this._ngStore.dispatch(new AllHandoverStandardsRequested())
    this._ngStore.pipe(select(selectAllHandoverStandards), takeUntil(this._onDestroy$)).subscribe(items => {
      this.handoverStandards = items ? items : []
    })

    this._ngStore.dispatch(new AllStateRepairsRequested())
    this._ngStore.pipe(select(selectAllStateRepairs), takeUntil(this._onDestroy$)).subscribe(items => {
      this.stateOfRepairs = items ? items : []
    })

    this._ngStore.dispatch(new AllFoundationTypesRequested())
    this._ngStore.pipe(select(selectAllFoundationTypes), takeUntil(this._onDestroy$)).subscribe(items => {
      this.foundationTypes = items ? items : []
    })

    this._ngStore.dispatch(new AllMainWallsRequested());
    this._ngStore.select(selectAllMainWalls).pipe(takeUntil(this._onDestroy$)).subscribe(res => {
      this.mainwalls = res ? res.filter(item => item.property_sub_type_id == 0) : []
    });

    this._ngStore.dispatch(new AllExternalFacadesRequested());
    this._ngStore.select(selectAllExternalFacades).pipe(takeUntil(this._onDestroy$)).subscribe(res => {
      this.externalFacades = res ? res.filter(item => item.property_sub_type_id == 0) : []
    })

    this._ngStore.dispatch(new AllRoofCoveringsRequested());
    this._ngStore.select(selectAllRoofCoverings).pipe(takeUntil(this._onDestroy$)).subscribe(res => {
      this.roofCoverings = res ? res.filter(item => item.property_sub_type_id == 0) : []
    });

    this._ngStore.dispatch(new AllUnitAreaMeasurementsRequested())
    this._ngStore.select(selectAllUnitAreaMeasurements).pipe(takeUntil(this._onDestroy$)).subscribe(res => {
      this.unitAreaMeasurements = res ? res : []
    })
  }

  ngOnDestroy(): void {
    this._onDestroy$.next()
    this._onDestroy$.complete()
  }

  identity(index: number, item: {uuid: string}) {
    return item.uuid
  }

  onTabChange(index: number) {
    this.selectedTab = index
  }

  onMapLocationFormChange(event: boolean | undefined) {
    const state = this._formState$.getValue()
    this._formState$.next({
      ...state,
      map_location: {
        invalid: event,
        requiredFields: 0
      }
    })
  }

  onMarkerChange(event: {lat: number, lng: number, isShown: boolean}) {
    if (event == null)  {
      return;
    }
    this._markerInfo$.next({
      lat: event.lat,
      lng: event.lng,
      isShow: event.isShown
    })
  }

  onCoordinatePointsChange(points: CoordinatePoint[]) {
    this._points$.next(points)
  }
  onLandAreasChange(items: LandArea[]) {
    this._landAreas$.next(items)
  }

  onConsiderationsChange(considerations: AssetClassTenure[]) {
    if (considerations.length > 0) {
      this.considerationError$.next(false)
    } else {
      this.considerationError$.next(undefined)
    }
  }

  onBuildingChange(building: AncillaryBuilding) {
    const currentBuildings = this._buildings$.getValue()
    const buildings = currentBuildings.map(item => {
      if (item.uuid === building.uuid) return building
      return item
    }) 
    this._buildings$.next(buildings)
  }

  submit(isComplete: boolean) {
    this.hasFormErrors = false
    this.showTitleRequiredError$.next(false)
    this.considerationError$.next(false)

    let formState = this._formState$.getValue()

    if (this.mapLocationComponent) {
      if (isComplete && this.mapLocationComponent.validate()) {
        this.hasFormErrors = true
        formState = {
          ...formState,
          map_location: {
            invalid: true,
            requiredFields: this.mapLocationComponent.errorFields.length
          }
        }
      } else {
        formState = {
          ...formState,
          map_location: {
            invalid: false,
            requiredFields: 0
          }
        }
      }
    } else {
      formState = {
        ...formState,
        map_location: {
          invalid: false,
          requiredFields: 0
        }
      }
    }

    if (isComplete && this.form.invalid) {
      this.form.controls.property_type_id.markAsTouched()
      this.hasFormErrors = true
      formState = {
        ...formState,
        about_property: {
          invalid: true,
          requiredFields: 1
        }
      }
    } else {
      formState = {
        ...formState,
        about_property: {
          invalid: false,
          requiredFields: 0
        }
      }
    }

    if (isComplete && this.sizesSubject.value.length == 0) {
      this.hasFormErrors = true;
      formState = {...formState, size: {
        requiredFields: 1,
        invalid: true
      }}
    } else {
      formState = {...formState, size: {
        requiredFields: 0,
        invalid: false
      }}
    }

    if (isComplete && !this.considerationAndSourceComponent.validate()) {
      this.hasFormErrors = true;
      this.considerationError$.next(true);
      formState = {
        ...formState,
        considerations: {
          invalid: true,
          requiredFields: 1
        }
      }
    } else {
      formState = {
        ...formState,
        considerations: {
          invalid: false,
          requiredFields: 1
        }
      }
    }

    if (isComplete && this.uploadFileComponent.uploadFiles.length == 0) {
    } else {
      let hasError = false
      const uploadedFiles = this.uploadFileComponent.uploadFiles;
      uploadedFiles.forEach(file => {
        if (file.title === null || file.title === '') {
          hasError = true
          this.hasFormErrors = true;
          this.showTitleRequiredError$.next(true);
        }
      })
      if (hasError) {
        formState = {
          ...formState,
          pictures: {
            invalid: true,
            requiredFields: 1
          }
        }
      } else {
        formState = {
          ...formState,
          pictures: {
            invalid: false,
            requiredFields: 0
          }
        }
      }
    }

    let esgIndicatorValues: EsgIndicator | null = null
    if (this.esgIndicatorComponent) {
      const esgIndicatorResult = this.esgIndicatorComponent.submit(!isComplete)
      if (esgIndicatorResult.type === 'success') {
        esgIndicatorValues = esgIndicatorResult.values
      } else if (esgIndicatorResult.type === 'error') {
        this.hasFormErrors = true
      }
    }

    this._formState$.next(formState)
    if (this.hasFormErrors) {
      this._layoutUtilsService.showFormError()
      return;
    }

    return this._prepareAssetClass(isComplete, esgIndicatorValues)
  }

  private _prepareAssetClass(isComplete: boolean, esgIndicator: EsgIndicator) {
    const assetClass = this._assetClass$.getValue()
    const ac = new SpecialPurposeAssetClass()
    ac.id = assetClass.id
    ac.status = isComplete ? 1 : 0
    ac.formType = this._formType$.getValue() == 'complex' ? 1 : 2
    ac.esgIndicator = esgIndicator

    const controls = this.form.controls

    if (this.mapLocationComponent) {
      ac.location_data = this.mapLocationComponent.getData()
    }

    const parcelIdentificationForm = controls.parcel_identification as ParcelIdentificationFormGroup
    const landAreaForm = parcelIdentificationForm.controls.land_area as LandAreaFormGroup
    const landArea = new AssetClassLandAreaModel()
    landArea.clear()
    landArea.id = assetClass.landArea.id
    landArea.planning_status_id = landAreaForm.controls.planning_status_id.value
    landArea.qos_degradation_id = landAreaForm.controls.qos_degradation_id.value
    landArea.env_considerations = landAreaForm.controls.env_considerations.value
    landArea.other_considerations = landAreaForm.controls.other_considerations.value
    ac.add_info_on_land = parcelIdentificationForm.controls.add_info_on_land.value;
    ac.land_parcel_name = parcelIdentificationForm.controls.land_parcel_name.value;
    ac.coordinate_reference_system_id = parcelIdentificationForm.controls.coordinate_reference_system_id.value;
    ac.boundary_type_id = parcelIdentificationForm.controls.boundary_type.value
    ac.points = this._points$.value.map(item => ({lat: item.latitude, lng: item.longitude}));
    ac.landArea = landArea;
    ac.landAreaComponents = this._landAreas$.value.map(item => mapLandAreaToAssetClassLandAreaComponent(item)) 

    ac.property_type_id = controls.property_type_id.value
    ac.completion_year = controls.completion_year.value
    ac.property_grade_id = controls.property_grade_id.value
    ac.building_name = controls.building_name.value
    ac.above_floors = Number(controls.above_floors.value)
    ac.below_floors = Number(controls.below_floors.value)
    ac.handover_standard_id = controls.handover_standard_id.value
    ac.state_of_repair_id = controls.state_of_repair_id.value
    ac.foundation_type_id = controls.foundation_type_id.value
    ac.developer = controls.developer.value
    ac.anchor_tenant = controls.anchor_tenant.value
    ac.has_ancillary_buildings = controls.has_ancillary_buildings.value
    ac.ancillary_buildings_num = controls.ancillary_buildings_num.value
    ac.construction_desc = controls.construction_desc.value

    ac.accommodation = this.accommodationLayoutTable ? {...this.accommodationLayoutTable.getData(), id: assetClass.accommodation.id} : {id: assetClass.accommodation.id, data: null, others: []};

    ac.serviceAndInfraData = this.servicesAndInfraComponent.getdata();

    if (this.externalAspectComponent) {
      ac.externalAspectData = this.externalAspectComponent.getData();
    }

    if (this.internalAspectComponent) {
      ac.internalAspectData = this.internalAspectComponent.getData();
    }
    ac.grounds = {...this.acGroundsComponent.getData(), id: assetClass.grounds.id};

    this.uploadFileComponent.res$.pipe(take(1)).subscribe(value => {
      if (value) {
        ac.picture = value.success;
      }
    })
    ac.sizes = this.sizesSubject.value;
    ac.buildings = this._buildings$.getValue()
    ac.tenures = this.considerationAndSourceComponent ? this.considerationAndSourceComponent.getData() : [];
    if (this.acDocumentUploadComponent) {
      ac.documents = this.acDocumentUploadComponent.getDocuments();
    }

    return {
      ac,
      fileList: this.uploadFileComponent ? this.uploadFileComponent.uploadFiles : []
    }
  }

  private _mapTabs(state: FormState) {
    let locationState = 'required'
    let locationStateRequiredFields = 0;
    if (state.map_location.invalid) {
      locationState = 'error'
      locationStateRequiredFields = state.map_location.requiredFields
    } else if (state.map_location.invalid == false) {
      locationState = 'complete'
    }

    let propertyInformationState = 'required'
    let propertyInformationRequiredFields = 0
    if (state.about_property.invalid || state.size.invalid) {
      propertyInformationState = 'error'
      propertyInformationRequiredFields = state.about_property.requiredFields + state.size.requiredFields
    } else if (state.about_property.invalid == false && state.size.invalid == false) {
      propertyInformationState = 'complete'
    }

    let considerationState = 'required'
    let considerationRequiredFields = 0
    if (state.considerations.invalid) {
      considerationState = 'error'
      considerationRequiredFields = state.considerations.requiredFields
    } else if (state.considerations.invalid == false) {
      considerationState = 'complete'
    }

    return [
      {
        label: 'Location & Building',
        state: locationState,
        requiredFields: locationStateRequiredFields
      },
      {
        label: 'Property Information',
        state: propertyInformationState,
        requiredFields: propertyInformationRequiredFields
      },
      {
        label: 'ESG Indicators',
        state: 'default'
      },
      {
        label: 'Lease & sale information',
        state: considerationState,
        requiredFields: considerationRequiredFields
      },
      {
        label: 'Pictures & documents',
        state: 'default'
      }
    ]
  }

  private _updateFormState() {
    const controlNames = ['property_type_id']
    const result = updateFormState(this.form, controlNames, this.isSavedBefore)
    let size: boolean | undefined = false
    if (this.isSavedBefore) {
      if (this.sizesSubject.value.length === 0) {
        size = undefined
      }
    } else {
      size = undefined
    }

    let consideration: boolean | undefined = false
    if (this.isSavedBefore) {
      if (this.tenuresSubject.value.length === 0) {
        consideration = undefined
      }
    } else {
      consideration = undefined
    }

    const state = this._formState$.getValue()
    this._formState$.next({
      ...state,
      about_property: {
        invalid: result,
        requiredFields: 0
      },
      size: {
        invalid: size,
        requiredFields: 0
      },
      considerations: {
        invalid: consideration,
        requiredFields: 0
      }
    })
  }
}

interface FormState {
  map_location: {
    invalid: boolean | undefined,
    requiredFields: number
  },
  about_property: {
    invalid: boolean | undefined,
    requiredFields: number
  },
  size: {
    invalid: boolean | undefined,
    requiredFields: number
  }
  considerations: {
    invalid: boolean | undefined,
    requiredFields: number
  },
  pictures: {
    invalid: boolean | undefined,
    requiredFields: number
  }
}

export function createAncillaryBuilding(): AncillaryBuilding {
  return new AncillaryBuilding();
}

function _deepCopy(arr: any[]): any[] {
  return arr.map(item => ({...item}));
}